import {
  ApiFactory,
  IProductAvailability,
  IProductAvailabilityChangedEvent,
} from '../api/easypos-api';
import { RealtimeConnection } from '../realtime/realtime-connection';

// Per-shop "currently sold out" overlay. The PWA holds the live snapshot
// in memory, refreshes on shop switch via GET /product-availability, and
// subscribes to the productAvailabilityChanged SignalR event so flips
// from other terminals appear within seconds.
//
// Callers (sales-new-page, product-edit-page, ...) read state synchronously
// via `getUnavailable(itemId)` and subscribe via `onChange(...)` for
// re-renders. Writes go straight to the API; the realtime event echoes
// back the local applyEvent(...) update so we don't double-render.
export class ProductAvailabilityStore {
  private _byItemId = new Map<string, IProductAvailability>();
  private _shopName?: string;
  private _unsubRealtime?: () => void;
  private readonly _listeners = new Set<() => void>();

  // Replace the shop being tracked. Pulls a fresh snapshot from the API
  // and (re-)subscribes the realtime channel handler. Pass undefined to
  // clear (logout / shop deselected).
  public async attachToShop(
    shopName: string | undefined,
    realtime: RealtimeConnection | undefined
  ): Promise<void> {
    this._unsubRealtime?.();
    this._unsubRealtime = undefined;
    this._byItemId.clear();
    this._shopName = shopName;
    this._notify();

    if (!shopName) return;

    const r =
      await ApiFactory.authenticated().listProductAvailability(shopName);
    if (r.success && r.data) {
      for (const a of r.data) {
        this._byItemId.set(this._key(a.itemId), a);
      }
      this._notify();
    }

    if (realtime) {
      this._unsubRealtime = realtime.on(
        'productAvailabilityChanged',
        (payload) => {
          this.applyEvent(payload as IProductAvailabilityChangedEvent);
        }
      );
    }
  }

  // Apply a single change locally. Public so the page can call this
  // optimistically after a successful POST/DELETE without waiting for
  // the realtime echo.
  public applyEvent(evt: IProductAvailabilityChangedEvent): void {
    if (!evt?.itemId) return;
    const key = this._key(evt.itemId);
    if (evt.isUnavailable) {
      this._byItemId.set(key, {
        itemId: key,
        isUnavailable: true,
        sinceUtc: evt.sinceUtc ?? new Date().toISOString(),
        byLoginId: evt.byLoginId ?? '',
        reason: evt.reason ?? 'out-of-stock',
        reasonNote: evt.reasonNote ?? null,
        unavailableUntilUtc: evt.unavailableUntilUtc ?? null,
      });
    } else {
      this._byItemId.delete(key);
    }
    this._notify();
  }

  public getUnavailable(itemId: string): IProductAvailability | undefined {
    return this._byItemId.get(this._key(itemId));
  }

  public isUnavailable(itemId: string): boolean {
    return this._byItemId.has(this._key(itemId));
  }

  public listUnavailable(): IProductAvailability[] {
    return Array.from(this._byItemId.values());
  }

  public onChange(listener: () => void): () => void {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  public get shopName(): string | undefined {
    return this._shopName;
  }

  private _key(itemId: string): string {
    return itemId.toLowerCase();
  }

  private _notify(): void {
    for (const l of this._listeners) {
      try {
        l();
      } catch (err) {
        console.error('[menu-availability] listener threw', err);
      }
    }
  }
}
